import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth';
import {
    User, Mail, Phone, Cake, Calendar, Clock, Activity, Gift, Award,
    ShieldCheck, ShieldOff, Plus, Minus, Send, Loader2, ScanLine,
    CheckCircle2, Sparkles, AlertTriangle, TrendingUp, Layers, Trash2
} from 'lucide-react';

const ACTION_META = {
    scan: { label: 'Scan effectué', icon: ScanLine, color: 'text-slate-500', bg: 'bg-slate-100' },
    tampon_ajoute: { label: 'Tampon ajouté', icon: Plus, color: 'text-amber-600', bg: 'bg-amber-100' },
    points_ajoutes: { label: 'Points ajoutés', icon: Plus, color: 'text-blue-600', bg: 'bg-blue-100' },
    points_retires: { label: 'Points retirés', icon: Minus, color: 'text-orange-600', bg: 'bg-orange-100' },
    achat_enregistre: { label: 'Achat enregistré', icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-100' },
    recompense_reclamee: { label: 'Récompense réclamée', icon: Gift, color: 'text-purple-600', bg: 'bg-purple-100' },
    cashback_utilise: { label: 'Cashback utilisé', icon: Sparkles, color: 'text-green-600', bg: 'bg-green-100' },
    cadeau_anniversaire: { label: 'Cadeau anniversaire', icon: Cake, color: 'text-pink-600', bg: 'bg-pink-100' },
    palier_reclame: { label: 'Palier réclamé', icon: Award, color: 'text-violet-600', bg: 'bg-violet-100' },
    notification_envoyee: { label: 'Notification envoyée', icon: Send, color: 'text-blue-500', bg: 'bg-blue-50' },
};

function ProgramStats({ wallet }) {
    const card = wallet.card;
    if (!card) return null;
    const type = card.type;
    const stats = [];
    if (type === 'stamp') stats.push({ k: 'Tampons', v: `${wallet.stamps || 0} / ${card.stamp_goal || '?'}` });
    if (type === 'points') stats.push({ k: 'Points', v: `${wallet.points || 0} / ${card.reward_points_cost || '?'}` });
    if (type === 'cashback') {
        stats.push({ k: 'Cashback', v: `${(wallet.cashback_balance || 0).toFixed(2)} €` });
        stats.push({ k: 'Dépensé', v: `${(wallet.total_spent || 0).toFixed(2)} €` });
    }
    if (type === 'amount') {
        stats.push({ k: 'Montant', v: `${(wallet.total_spent || 0).toFixed(2)} € / ${card.amount_goal || '?'} €` });
    }
    if (type === 'tier') {
        const pts = wallet.points || 0;
        const paliers = (card.paliers_config || []).sort((a, b) => a.points - b.points);
        const reached = paliers.filter(p => p.points <= pts).length;
        stats.push({ k: 'Points', v: pts });
        stats.push({ k: 'Paliers atteints', v: `${reached} / ${paliers.length}` });
        stats.push({ k: 'Dépensé', v: `${(wallet.total_spent || 0).toFixed(2)} €` });
    }
    stats.push({ k: 'Récompenses', v: wallet.rewards_claimed || 0 });
    return (
        <div className="grid grid-cols-2 gap-2 text-xs">
            {stats.map(s => (
                <div key={s.k} className="bg-slate-50 rounded-lg px-2.5 py-1.5">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide">{s.k}</p>
                    <p className="font-semibold text-slate-800">{s.v}</p>
                </div>
            ))}
        </div>
    );
}

function ProgramActions({ wallet, onAction, busyKey }) {
    const [pointsInput, setPointsInput] = useState('');
    const [purchaseInput, setPurchaseInput] = useState('');
    const card = wallet.card;
    if (!card) return null;
    const type = card.type;

    const canRedeem =
        type === 'stamp' ? (wallet.stamps || 0) >= (card.stamp_goal || 0) :
        type === 'points' ? (wallet.points || 0) >= (card.reward_points_cost || 0) :
        type === 'cashback' ? (wallet.cashback_balance || 0) >= (card.cashback_min_redeem || 0) :
        type === 'amount' ? !!wallet.reward_pending : false;

    const paliersAvailable = type === 'tier'
        ? (card.paliers_config || []).filter(p => (wallet.points || 0) >= p.points).sort((a, b) => b.points - a.points)
        : [];

    return (
        <div className="space-y-2.5">
            {/* STAMP actions */}
            {type === 'stamp' && (
                <div className="flex gap-2">
                    <Button size="sm" className="flex-1 bg-amber-500 hover:bg-amber-600 text-white" disabled={!!busyKey} onClick={() => onAction({ type: 'add_stamp', amount: 1 })} data-testid={`action-add-stamp-${wallet.id}`}>
                        {busyKey === `${wallet.id}:add_stamp` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" />Tampon</>}
                    </Button>
                    {canRedeem && (
                        <Button size="sm" className="flex-1 bg-purple-500 hover:bg-purple-600 text-white" disabled={!!busyKey} onClick={() => onAction({ type: 'redeem' })} data-testid={`action-redeem-${wallet.id}`}>
                            {busyKey === `${wallet.id}:redeem` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Gift className="w-3.5 h-3.5 mr-1" />Récompense</>}
                        </Button>
                    )}
                </div>
            )}

            {/* POINTS actions */}
            {type === 'points' && (
                <>
                    <div className="flex items-center gap-2">
                        <Input type="number" min="1" placeholder="Points" value={pointsInput} onChange={e => setPointsInput(e.target.value)} className="h-8 text-sm flex-1" data-testid={`points-input-${wallet.id}`} />
                        <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white shrink-0" disabled={!!busyKey || !pointsInput} onClick={() => { onAction({ type: 'add_points', amount: parseInt(pointsInput, 10) || 0 }); setPointsInput(''); }} data-testid={`action-add-points-${wallet.id}`}>
                            <Plus className="w-3.5 h-3.5 mr-1" />Ajouter
                        </Button>
                        <Button size="sm" variant="outline" className="text-orange-600 border-orange-200 hover:bg-orange-50 shrink-0" disabled={!!busyKey || !pointsInput} onClick={() => { onAction({ type: 'remove_points', amount: parseInt(pointsInput, 10) || 0 }); setPointsInput(''); }} data-testid={`action-remove-points-${wallet.id}`}>
                            <Minus className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                    {canRedeem && (
                        <Button size="sm" className="w-full bg-purple-500 hover:bg-purple-600 text-white" disabled={!!busyKey} onClick={() => onAction({ type: 'redeem' })} data-testid={`action-redeem-${wallet.id}`}>
                            {busyKey === `${wallet.id}:redeem` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Gift className="w-3.5 h-3.5 mr-1" />Donner récompense</>}
                        </Button>
                    )}
                </>
            )}

            {/* CASHBACK / AMOUNT / TIER (purchase-based) */}
            {(type === 'cashback' || type === 'amount' || type === 'tier') && (
                <>
                    <div className="flex items-center gap-2">
                        <Input type="number" min="0.01" step="0.01" placeholder="Montant (€)" value={purchaseInput} onChange={e => setPurchaseInput(e.target.value)} className="h-8 text-sm flex-1" data-testid={`purchase-input-${wallet.id}`} />
                        <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white shrink-0" disabled={!!busyKey || !purchaseInput} onClick={() => { onAction({ type: 'add_purchase', amount: parseFloat(purchaseInput) || 0 }); setPurchaseInput(''); }} data-testid={`action-add-purchase-${wallet.id}`}>
                            <TrendingUp className="w-3.5 h-3.5 mr-1" />Enregistrer
                        </Button>
                    </div>
                    {type === 'tier' && paliersAvailable.length > 0 && (
                        <div className="space-y-1.5">
                            <p className="text-[10px] font-semibold text-violet-600 uppercase tracking-wide">Paliers disponibles</p>
                            {paliersAvailable.map(p => (
                                <Button key={p.points} size="sm" variant="outline" className="w-full justify-between border-violet-200 text-violet-700 hover:bg-violet-50" disabled={!!busyKey} onClick={() => onAction({ type: 'redeem_palier', amount: p.points })} data-testid={`action-redeem-palier-${wallet.id}-${p.points}`}>
                                    <span className="truncate text-xs">{p.reward}</span>
                                    <span className="text-[10px] font-bold shrink-0">−{p.points} pts</span>
                                </Button>
                            ))}
                        </div>
                    )}
                    {(type === 'cashback' || type === 'amount') && canRedeem && (
                        <Button size="sm" className="w-full bg-purple-500 hover:bg-purple-600 text-white" disabled={!!busyKey} onClick={() => onAction({ type: type === 'cashback' ? 'use_cashback' : 'redeem' })} data-testid={`action-redeem-${wallet.id}`}>
                            {type === 'cashback' ? <><Sparkles className="w-3.5 h-3.5 mr-1" />Utiliser cashback</> : <><Gift className="w-3.5 h-3.5 mr-1" />Donner récompense</>}
                        </Button>
                    )}
                </>
            )}
        </div>
    );
}

export default function CustomerDetailDialog({ open, customerId, onClose, onChanged }) {
    const { user } = useAuth();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('summary');
    const [busyKey, setBusyKey] = useState(null);
    const [pushForm, setPushForm] = useState({ title: '', message: '' });
    const [sendingPush, setSendingPush] = useState(false);

    const canPush = user?.plan === 'pro' || user?.plan === 'premium';

    const load = useCallback(async () => {
        if (!customerId) return;
        setLoading(true);
        try {
            const res = await api.get(`/customers/${customerId}/details`);
            setDetail(res.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur de chargement');
            onClose?.();
        } finally {
            setLoading(false);
        }
    }, [customerId, onClose]);

    useEffect(() => {
        if (open && customerId) {
            setActiveTab('summary');
            setPushForm({ title: '', message: '' });
            load();
        }
    }, [open, customerId, load]);

    const handleAction = async (wallet, action) => {
        const key = `${wallet.id}:${action.type}`;
        setBusyKey(key);
        try {
            await api.post('/transactions', {
                wallet_id: wallet.id,
                type: action.type,
                amount: action.amount ?? 0,
                description: action.description || '',
            });
            const labels = {
                add_stamp: 'Tampon ajouté',
                add_points: `+${action.amount} pts`,
                remove_points: `−${action.amount} pts`,
                add_purchase: `Achat de ${action.amount}€ enregistré`,
                redeem: 'Récompense donnée',
                use_cashback: 'Cashback utilisé',
                redeem_palier: `Palier réclamé (−${action.amount} pts)`,
            };
            toast.success(labels[action.type] || 'Action effectuée');
            await load();
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setBusyKey(null);
        }
    };

    const handleSendPush = async () => {
        if (!pushForm.title.trim() || !pushForm.message.trim()) {
            toast.error('Titre et message requis');
            return;
        }
        setSendingPush(true);
        try {
            const res = await api.post(`/customers/${customerId}/notify`, pushForm);
            toast.success(res.data.push_recipients > 0
                ? `Notification envoyée (${res.data.push_recipients} appareil(s))`
                : 'Message envoyé (in-app uniquement)');
            setPushForm({ title: '', message: '' });
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSendingPush(false);
        }
    };

    const c = detail?.customer;
    const stats = detail?.stats;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
            <DialogContent className="rounded-2xl sm:max-w-2xl p-0 overflow-hidden" data-testid="customer-detail-dialog">
                {loading || !detail ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <Loader2 className="w-6 h-6 animate-spin text-emerald-500 mb-2" />
                        <p className="text-xs text-slate-400">Chargement du client...</p>
                    </div>
                ) : (
                    <div className="flex flex-col max-h-[85vh]">
                        {/* Header */}
                        <DialogHeader className="bg-gradient-to-br from-emerald-50 to-white px-5 pt-5 pb-4 border-b border-slate-100">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center shrink-0">
                                    <span className="text-lg font-bold text-white">{(c.name || '?').charAt(0).toUpperCase()}</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <DialogTitle className="text-base font-heading text-left truncate">{c.name}</DialogTitle>
                                    <p className="text-xs text-slate-500 truncate">{c.email}</p>
                                </div>
                                {c.rgpd_consent ? (
                                    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] shrink-0"><ShieldCheck className="w-3 h-3 mr-0.5" />RGPD</Badge>
                                ) : (
                                    <Badge className="bg-slate-100 text-slate-500 border-0 text-[10px] shrink-0"><ShieldOff className="w-3 h-3 mr-0.5" />Non consenti</Badge>
                                )}
                            </div>

                            {/* Tabs */}
                            <div className="flex gap-1 mt-3 bg-white border border-slate-200 rounded-lg p-0.5 self-start">
                                {[
                                    { v: 'summary', l: 'Résumé', icon: User },
                                    { v: 'activity', l: 'Activité', icon: Activity },
                                    { v: 'actions', l: 'Actions', icon: Sparkles },
                                ].map(t => {
                                    const I = t.icon;
                                    return (
                                        <button key={t.v} onClick={() => setActiveTab(t.v)} className={`flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-md transition-all ${activeTab === t.v ? 'bg-emerald-500 text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`tab-${t.v}`}>
                                            <I className="w-3 h-3" />{t.l}
                                        </button>
                                    );
                                })}
                            </div>
                        </DialogHeader>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto px-5 py-4">
                            {/* RESUME */}
                            {activeTab === 'summary' && (
                                <div className="space-y-4">
                                    {/* Quick stats */}
                                    <div className="grid grid-cols-4 gap-2">
                                        <div className="bg-emerald-50 rounded-xl p-2.5 text-center">
                                            <Layers className="w-4 h-4 mx-auto text-emerald-500 mb-1" />
                                            <p className="text-base font-bold text-emerald-700">{stats.programs_count}</p>
                                            <p className="text-[10px] text-slate-500">Programmes</p>
                                        </div>
                                        <div className="bg-blue-50 rounded-xl p-2.5 text-center">
                                            <ScanLine className="w-4 h-4 mx-auto text-blue-500 mb-1" />
                                            <p className="text-base font-bold text-blue-700">{stats.total_scans}</p>
                                            <p className="text-[10px] text-slate-500">Scans</p>
                                        </div>
                                        <div className="bg-purple-50 rounded-xl p-2.5 text-center">
                                            <Gift className="w-4 h-4 mx-auto text-purple-500 mb-1" />
                                            <p className="text-base font-bold text-purple-700">{stats.total_rewards_claimed}</p>
                                            <p className="text-[10px] text-slate-500">Récompenses</p>
                                        </div>
                                        <div className="bg-amber-50 rounded-xl p-2.5 text-center">
                                            <TrendingUp className="w-4 h-4 mx-auto text-amber-500 mb-1" />
                                            <p className="text-base font-bold text-amber-700">{stats.total_spent.toFixed(0)}€</p>
                                            <p className="text-[10px] text-slate-500">Dépensé</p>
                                        </div>
                                    </div>

                                    {/* Personal info */}
                                    <div className="border border-slate-100 rounded-xl p-3 space-y-1.5">
                                        <p className="text-[10px] font-semibold uppercase text-slate-400 tracking-wide mb-1">Informations</p>
                                        <div className="flex items-center gap-2 text-xs text-slate-600">
                                            <Mail className="w-3.5 h-3.5 text-slate-400" />{c.email}
                                        </div>
                                        {c.rgpd_consent && c.phone && (
                                            <div className="flex items-center gap-2 text-xs text-slate-600">
                                                <Phone className="w-3.5 h-3.5 text-blue-400" />{c.phone}
                                            </div>
                                        )}
                                        {c.rgpd_consent && c.birthday && (
                                            <div className="flex items-center gap-2 text-xs text-slate-600">
                                                <Cake className="w-3.5 h-3.5 text-pink-400" />{c.birthday.split('-').reverse().join('/')}
                                            </div>
                                        )}
                                        {detail.joined_at && (
                                            <div className="flex items-center gap-2 text-xs text-slate-600">
                                                <Calendar className="w-3.5 h-3.5 text-emerald-400" />Inscrit le {new Date(detail.joined_at).toLocaleDateString('fr-FR')}
                                            </div>
                                        )}
                                        {!c.rgpd_consent && (
                                            <p className="text-[11px] text-amber-600 flex items-center gap-1.5 pt-1">
                                                <AlertTriangle className="w-3 h-3" />Téléphone et anniversaire non accessibles (consentement RGPD requis)
                                            </p>
                                        )}
                                    </div>

                                    {/* Programs */}
                                    <div className="space-y-2.5">
                                        <p className="text-[10px] font-semibold uppercase text-slate-400 tracking-wide">Programmes suivis ({detail.wallets.length})</p>
                                        {detail.wallets.map(w => (
                                            <div key={w.id} className="border border-slate-100 rounded-xl overflow-hidden" data-testid={`program-${w.id}`}>
                                                <div className="px-3 py-2 flex items-center gap-2.5" style={{ background: `linear-gradient(90deg, ${w.card?.card_color_from || '#10b981'}, ${w.card?.card_color_to || '#059669'})` }}>
                                                    <span className="text-xs font-bold text-white truncate flex-1">{w.card?.name || 'Programme'}</span>
                                                    <Badge className="bg-white/25 text-white border-0 text-[10px] uppercase">{w.card?.type}</Badge>
                                                </div>
                                                <div className="p-3">
                                                    <ProgramStats wallet={w} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* ACTIVITY */}
                            {activeTab === 'activity' && (
                                <div className="space-y-1.5">
                                    {detail.logs.length === 0 ? (
                                        <p className="text-sm text-slate-400 text-center py-8">Aucune activité enregistrée.</p>
                                    ) : detail.logs.map(log => {
                                        const meta = ACTION_META[log.action] || { label: log.action, icon: Activity, color: 'text-slate-500', bg: 'bg-slate-100' };
                                        const I = meta.icon;
                                        const date = log.created_at ? new Date(log.created_at) : null;
                                        return (
                                            <div key={log.id || log.created_at} className="flex items-start gap-2.5 py-2 border-b border-slate-50 last:border-0" data-testid={`log-${log.id || log.created_at}`}>
                                                <div className={`w-7 h-7 rounded-full ${meta.bg} flex items-center justify-center shrink-0`}>
                                                    <I className={`w-3.5 h-3.5 ${meta.color}`} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-medium text-slate-800">{meta.label}</p>
                                                    {log.card_name && <p className="text-[11px] text-slate-500 truncate">{log.card_name}</p>}
                                                    {log.details?.amount !== undefined && log.details?.amount !== 0 && (
                                                        <p className="text-[11px] text-slate-500">Montant : <span className="font-semibold">{log.details.amount}</span></p>
                                                    )}
                                                    {log.details?.title && <p className="text-[11px] text-slate-500 truncate">« {log.details.title} »</p>}
                                                </div>
                                                {date && (
                                                    <span className="text-[10px] text-slate-400 shrink-0 whitespace-nowrap">
                                                        {date.toLocaleDateString('fr-FR')} {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* ACTIONS */}
                            {activeTab === 'actions' && (
                                <div className="space-y-4">
                                    {/* Per-program actions */}
                                    <div className="space-y-3">
                                        <p className="text-[10px] font-semibold uppercase text-slate-400 tracking-wide">Modifier les programmes</p>
                                        {detail.wallets.map(w => (
                                            <div key={w.id} className={`border rounded-xl overflow-hidden ${w.card?.admin_disabled ? 'border-red-200 bg-red-50/30' : 'border-slate-100'}`} data-testid={`action-card-${w.id}`}>
                                                <div className="px-3 py-2 flex items-center justify-between" style={{ background: w.card?.admin_disabled ? '#fee2e2' : `linear-gradient(90deg, ${w.card?.card_color_from || '#10b981'}, ${w.card?.card_color_to || '#059669'})` }}>
                                                    <span className={`text-xs font-bold truncate ${w.card?.admin_disabled ? 'text-red-700' : 'text-white'}`}>{w.card?.name}</span>
                                                    <Badge className={`border-0 text-[10px] ${w.card?.admin_disabled ? 'bg-red-200 text-red-800' : 'bg-white/25 text-white'}`}>{w.card?.type}</Badge>
                                                </div>
                                                <div className="p-3">
                                                    {w.card?.admin_disabled ? (
                                                        <div className="flex items-start gap-2 text-xs text-red-600">
                                                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                                            <span>Programme désactivé par l'administrateur — actions bloquées.</span>
                                                        </div>
                                                    ) : (
                                                        <ProgramActions wallet={w} busyKey={busyKey} onAction={(action) => handleAction(w, action)} />
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Push notification */}
                                    <div className="border border-slate-100 rounded-xl p-3 space-y-2.5">
                                        <div className="flex items-center gap-2">
                                            <Send className="w-4 h-4 text-blue-500" />
                                            <p className="text-sm font-semibold text-slate-800">Envoyer une notification</p>
                                            {!canPush && <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">PRO requis</Badge>}
                                        </div>
                                        {!canPush ? (
                                            <p className="text-xs text-slate-500">Les notifications individuelles sont disponibles avec un forfait Pro ou Premium.</p>
                                        ) : (
                                            <>
                                                <div>
                                                    <Label className="text-[11px]">Titre (max 80)</Label>
                                                    <Input value={pushForm.title} maxLength={80} onChange={e => setPushForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Promo spéciale pour vous !" className="h-8 text-sm" data-testid="push-title-input" />
                                                </div>
                                                <div>
                                                    <Label className="text-[11px]">Message (max 300)</Label>
                                                    <textarea value={pushForm.message} maxLength={300} onChange={e => setPushForm(f => ({ ...f, message: e.target.value }))} placeholder="Ex: -20% sur votre prochain achat" rows={2} className="w-full text-sm border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200" data-testid="push-message-input" />
                                                </div>
                                                <Button size="sm" className="w-full bg-blue-500 hover:bg-blue-600 text-white" disabled={sendingPush || !pushForm.title.trim() || !pushForm.message.trim()} onClick={handleSendPush} data-testid="send-push-btn">
                                                    {sendingPush ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" />Envoyer à {c.name}</>}
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
